import { useRef, useState } from 'react';
import styled from '@emotion/styled';
import { Close } from '@mui/icons-material';
import bytesToMegabytes from 'Utils/BytesToMegaBytes';
import { uiTheme as t } from './theme';

/** `cover` (16:9) para una portada; `compact` para un campo dentro de un formulario. */
export type FileDropZoneSize = 'cover' | 'compact';

export interface FileDropZoneLabels {
  prompt: string;
  browse: string;
  remove: string;
  hint?: string;
}

export interface FileDropZoneProps {
  onFile: (file: File) => void;
  /** Archivo elegido. Con él, el recuadro lo muestra en vez del mensaje. */
  file?: File | null;
  onRemove?: () => void;
  accept?: string;
  size?: FileDropZoneSize;
  labels: FileDropZoneLabels;
  disabled?: boolean;
}

const Zone = styled.div<{ over: boolean; disabled: boolean; size: FileDropZoneSize }>(({ over, disabled, size }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '1rem',
  width: '100%',
  boxSizing: 'border-box',
  ...(size === 'cover' ? { aspectRatio: '16 / 9' } : {}),
  minHeight: '9rem',
  padding: '1rem',
  textAlign: 'center',
  border: `1.5px dashed ${over ? t.color.primary : t.color.dropBorder}`,
  borderRadius: t.radius.sm,
  background: over ? t.color.accentSoft : t.color.pageBg,
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.7 : 1,
  transition: 'border-color 0.15s ease, background 0.15s ease',
}));

const Circle = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  minHeight: '2.75rem',
  padding: '0.75rem',
  aspectRatio: '1 / 1',
  borderRadius: '999px',
  background: t.color.accentSoft,
  color: t.color.primary,
  fontSize: '1.25rem',
  lineHeight: 1,
});

const Texts = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '0.1875rem',
  fontFamily: t.font,
});

const Main = styled.span({
  fontSize: '0.875rem',
  color: t.color.text,
});

const Sub = styled.span({
  fontSize: '0.8125rem',
  color: t.color.muted,
});

const Link = styled.span({
  color: t.color.primary,
});

const Hint = styled.span({
  fontSize: '0.6875rem',
  color: t.color.muted,
});

const FileName = styled.span({
  fontSize: '0.875rem',
  fontWeight: 600,
  color: t.color.text,
  overflowWrap: 'anywhere',
});

const RemoveButton = styled.button({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  border: 'none',
  background: 'transparent',
  color: t.color.muted,
  cursor: 'pointer',
  '& .MuiSvgIcon-root': { fontSize: 18 },
  '&:hover': { color: t.color.danger },
});

const HiddenInput = styled.input({
  display: 'none',
});

/**
 * Zona de arrastre para elegir un archivo.
 *
 * El texto llega por `labels` y no vive aquí: el mismo recuadro sirve para la
 * portada de un curso y para el PDF de una evidencia, y anunciar "tu imagen"
 * en el segundo caso es mentir. `size` decide si el recuadro guarda la
 * proporción de una portada o si es un campo más del formulario.
 */
const FileDropZone = ({
  onFile,
  file = null,
  onRemove,
  accept = 'image/jpeg,image/png',
  size = 'cover',
  labels,
  disabled = false,
}: FileDropZoneProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);

  /** Abre el explorador desde teclado o clic. */
  const openFilePicker = () => {
    if (!disabled) {
      inputRef.current?.click();
    }
  };

  /** Toma el primer archivo disponible y lo entrega al consumidor. */
  const take = (files: FileList | null) => {
    if (!disabled && files && files[0]) {
      onFile(files[0]);
    }
  };

  return (
    <>
      <Zone
        data-testid="drop-zone"
        data-size={size}
        over={over}
        disabled={disabled}
        size={size}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        onClick={openFilePicker}
        onKeyDown={event => {
          if (disabled) {
            return;
          }

          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openFilePicker();
          }
        }}
        onDragOver={event => {
          event.preventDefault();
          if (!disabled) {
            setOver(true);
          }
        }}
        onDragLeave={() => setOver(false)}
        onDrop={event => {
          event.preventDefault();
          setOver(false);
          take(event.dataTransfer.files);
        }}
      >
        <Circle aria-hidden="true">↑</Circle>
        <Texts>
          {file ? (
            <>
              <FileName>{file.name}</FileName>
              <Sub>{bytesToMegabytes(file.size, 2)} MB</Sub>
            </>
          ) : (
            <>
              <Main>{labels.prompt}</Main>
              <Sub>
                <Link>{labels.browse}</Link>
              </Sub>
              {labels.hint && <Hint>{labels.hint}</Hint>}
            </>
          )}
        </Texts>
        {file && onRemove ? (
          <RemoveButton
            type="button"
            aria-label={labels.remove}
            onClick={event => {
              // Sin esto, el clic sube a la zona y abre el explorador al quitar.
              event.stopPropagation();
              onRemove();
            }}
          >
            <Close />
          </RemoveButton>
        ) : null}
      </Zone>
      <HiddenInput
        ref={inputRef}
        data-testid="drop-input"
        type="file"
        accept={accept}
        disabled={disabled}
        onChange={event => take(event.target.files)}
      />
    </>
  );
};

export default FileDropZone;
